import {
  EventType,
  IAppointmentSuggestion,
  IEvent,
  IEventable,
  IEventTimePeriod,
  IImplementsTreatmentTemplateWithResolvedStaffer,
  IPractice,
  isPreBlockEvent,
  IStaffer,
  IStafferDurationMap,
  ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  isInRange,
  isSameRef,
  ITimePeriod,
  snapshot,
  timePeriodsIntersect,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact, differenceBy, first, groupBy, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { AppointmentSuggestion } from '../appointment/appointment-suggestion';
import { EventableQueries } from '../eventable-queries';
import { buildStafferAvailableTimes } from './brand-event-option-finder';
import { CalendarEvent } from './calendar-event';
import { Event } from './event';

export interface IAppointmentSuggestionResults {
  practitioner: WithRef<IStaffer>;
  suggestions: IAppointmentSuggestion[];
}

function eventPeriodToSuggestion(
  staffer: WithRef<IStaffer>,
  eventPeriod: IEventTimePeriod,
  preferredEvent: IEvent,
  scoreMin: number,
  treatmentCategories: DocumentReference<ITreatmentCategory>[]
): IAppointmentSuggestion | undefined {
  const suggestions = AppointmentSuggestion.convertOptionsToSuggestions(
    staffer,
    [eventPeriod],
    [],
    preferredEvent,
    [],
    undefined,
    treatmentCategories
  )
    .map((suggestion) => ({
      ...suggestion,
      event: {
        ...suggestion.event,
        type: EventType.AppointmentRequest,
      },
    }))
    .filter((suggestion) => suggestion.score >= scoreMin);

  return first(suggestions);
}

function toAppointmentSuggestions(
  eventPeriods: IEventTimePeriod[],
  staffMap: IStafferDurationMap[],
  scoreMin: number,
  treatmentCategories: DocumentReference<ITreatmentCategory>[],
  practice: WithRef<IPractice>
): IAppointmentSuggestion[] {
  const now = moment.tz(practice.settings.timezone);

  return compact(
    eventPeriods.map((eventPeriod) => {
      const preferredEvent = Event.init({
        from: toTimestamp(now.clone()),
        to: toTimestamp(now.clone().add('60', 'minutes')),
        practice: eventPeriod.practice,
      });
      const foundMap = staffMap.find((stafferMap) =>
        isSameRef(stafferMap.staffer, eventPeriod.staffer)
      );

      if (!foundMap) {
        return;
      }

      preferredEvent.to = toTimestamp(now.clone().add(foundMap.duration));

      return eventPeriodToSuggestion(
        foundMap.staffer,
        eventPeriod,
        preferredEvent,
        scoreMin,
        treatmentCategories
      );
    })
  );
}

export async function buildStaffOnlineBookingTimes(
  implementors: Pick<
    IImplementsTreatmentTemplateWithResolvedStaffer,
    'staffer' | 'stafferMap'
  >[],
  timePeriod: ITimePeriod,
  practice: WithRef<IPractice>,
  restrictToPreBlocks: boolean
): Promise<IAppointmentSuggestionResults[]> {
  const emptyImplementors = implementors.filter(
    ({ stafferMap }) =>
      !stafferMap.duration || stafferMap.duration.asMinutes() === 0
  );
  const emptyStaffSuggestions = emptyImplementors.map(({ staffer }) => ({
    practitioner: staffer,
    suggestions: [],
  }));

  const implementorsToSearch = differenceBy(
    implementors,
    emptyImplementors,
    (implementor) => implementor.staffer.ref.path
  );

  const availableTimes = await getAvailableTimes(
    implementorsToSearch,
    timePeriod,
    practice,
    implementorsToSearch.flatMap(
      ({ stafferMap }) => stafferMap.forcastEventables
    ),
    restrictToPreBlocks
  );

  const treatmentCategories = uniqBy(
    implementorsToSearch.flatMap(
      ({ stafferMap }) => stafferMap.treatmentCategories
    ),
    (category) => category.path
  );

  const suggestions = toAppointmentSuggestions(
    availableTimes,
    implementors.map(({ stafferMap }) => stafferMap),
    1,
    treatmentCategories,
    practice
  );

  const groupedSuggestions = groupBy(
    suggestions,
    (suggestion) => suggestion.practitioner.ref.id
  );

  const results = implementorsToSearch.map(({ staffer }) => ({
    practitioner: staffer,
    suggestions: groupedSuggestions[staffer.ref.id] ?? [],
  }));

  return [...results, ...emptyStaffSuggestions];
}

async function getAvailableTimes(
  implementors: Pick<
    IImplementsTreatmentTemplateWithResolvedStaffer,
    'staffer' | 'stafferMap'
  >[],
  timePeriod: ITimePeriod,
  practice: WithRef<IPractice>,
  forcastEventables: IEventable[],
  restrictToPreBlocks: boolean
): Promise<IEventTimePeriod[]> {
  const summaryEventables = await snapshot(
    EventableQueries.getScheduleSummaryEventablesWithFallback$(
      timePeriod,
      practice,
      implementors.map(({ staffer }) => staffer),
      implementors
        .map(({ stafferMap }) => stafferMap)
        .reduce(
          (maps, stafferMap) => ({
            ...maps,
            [stafferMap.staffer.ref.id]: stafferMap.rosters,
          }),
          {}
        ),
      [EventType.GapCandidate, EventType.Gap]
    )
  );

  const eventables = [...summaryEventables, ...forcastEventables].filter(
    (eventable) => {
      const event = Event.toTimePeriod(eventable.event);
      return (
        isInRange(timePeriod, event.from) ||
        isInRange(timePeriod, event.to) ||
        timePeriodsIntersect(timePeriod, event, false, 'minutes')
      );
    }
  );

  const flattenedMultiStaffEventables = eventables.flatMap((eventable) => {
    const staff = Event.staff(eventable.event);
    if (staff.length <= 1) {
      return [
        {
          ...eventable,
          staffer: staff[0],
        },
      ];
    }

    return staff.map((staffer) => ({
      ...eventable,
      staffer,
    }));
  });

  const groupedEventables = implementors
    .map(({ staffer }) => ({
      [staffer.ref.path]:
        flattenedMultiStaffEventables.filter((eventable) =>
          isSameRef(eventable.staffer, staffer)
        ) ?? [],
    }))
    .reduce((all, map) => ({ ...all, ...map }), {});

  return Object.entries(groupedEventables)
    .map(([stafferPath, stafferGroupedEventables]) => ({
      stafferPath,
      stafferGroupedEventables,
    }))
    .map(({ stafferPath, stafferGroupedEventables }) => {
      const stafferMap = implementors
        .flatMap((implementor) => implementor.stafferMap)
        .find((map) => map.staffer.ref.path === stafferPath);

      if (!stafferMap) {
        return [];
      }

      const stafferEventables = {
        preBlocks: getPreBlockEventables(stafferGroupedEventables),
        usedTimes: getUsedTimes(
          stafferGroupedEventables,
          stafferMap.treatmentCategories
        ),
      };

      if (restrictToPreBlocks) {
        if (!stafferEventables.preBlocks.length) {
          return [];
        }

        return buildStafferAvailableTimes(
          stafferEventables.preBlocks,
          stafferEventables.usedTimes,
          stafferMap.increment,
          practice,
          stafferMap.staffer,
          stafferMap.duration
        );
      }

      const rosteredTimesInRange = stafferMap.rosteredTimes.filter(
        (rosteredTime) =>
          isInRange(timePeriod, rosteredTime.from) ||
          isInRange(timePeriod, rosteredTime.to)
      );

      return buildStafferAvailableTimes(
        rosteredTimesInRange,
        stafferEventables.usedTimes,
        stafferMap.increment,
        practice,
        stafferMap.staffer,
        stafferMap.duration
      );
    })
    .flat();
}

function getUsedTimes(
  eventables: IEventable[],
  treatmentCategories: DocumentReference<ITreatmentCategory>[]
): IEventable[] {
  return eventables
    .filter((eventable) => {
      if (!isPreBlockEvent(eventable.event)) {
        return true;
      }

      const preBlock = eventable.event;

      if (!preBlock.isPublic) {
        return true;
      }

      if (!preBlock.allowedTreatmentCategories.length) {
        return false;
      }

      if (!treatmentCategories.length) {
        return true;
      }

      return preBlock.allowedTreatmentCategories.every((category) =>
        treatmentCategories.every(
          (treatmentCategory) => !isSameRef(category, treatmentCategory)
        )
      );
    })
    .map((eventable) =>
      CalendarEvent.init({
        event: eventable.event,
        isBlocking: true,
      })
    );
}

function getPreBlockEventables(eventables: IEventable[]): ITimePeriod[] {
  return eventables
    .filter(
      (eventable) =>
        isPreBlockEvent(eventable.event) && eventable.event.isPublic
    )
    .map((eventable) => Event.toTimePeriod(eventable.event));
}
